import { useState, useCallback } from 'react'
import NumberFormat from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { ModalBody, ModalHeader, Input } from 'reactstrap'
import styled from 'styled-components'

import { Modal, RadioButtonGroup } from '../../common-components'

import ButtonWithLoading from 'e-submission/components/ButtonWithLoading'
import { setNotificationMessage } from 'quick-quote/notification/actions'
import { isIDCardNo, isPassport } from 'e-submission/utils/pattern'
import NumberInputSearch from 'e-submission/components/Form/Input/NumberInputSearch'
import { setProcessingSuccess, searchAppListById } from '../actions'

const ConfirmButton = ({ onExit, isDisabledButton, selectedOption, idNoInputText, callingApi, onSearch }) => {
  const css = !isDisabledButton ? 'primary' : 'btn btn-disabled'

  const handleConfirm = useCallback(async () => {
    try {
      await onSearch({
        idType: selectedOption,
        idNo: idNoInputText,
      })
    } catch (error) {
      // Handle error silently
    }
  }, [selectedOption, idNoInputText, onExit])

  return (
    <div style={{ textAlign: 'center' }}>
      <ButtonWithLoading disabled={callingApi} color={css} className={css} onClick={handleConfirm}>
        ยืนยัน
      </ButtonWithLoading>
    </div>
  )
}

const DialogModalHeader = (props) => {
  const { onExit } = props
  return (
    <ModalHeader
      toggle={() => {
        props.setProcessingSuccess()
        onExit()
      }}
    >
      กรุณาระบุเลขที่บัตรประชาชน หรือ เลขที่หนังสือเดินทาง
    </ModalHeader>
  )
}

const RenderInputText = (props) => {
  // Passport Input
  const { idNoInputText, setIdNoInputText, setButtonDisable } = props

  return (
    <NumberInputSearch
      id={'search.passport.id'}
      isTextInput
      value={idNoInputText}
      disabled={false}
      type="text"
      isAllowed={({ value }) => value.length <= 13}
      validatetion={[
        {
          isValid: idNoInputText.length > 0 && idNoInputText.length < 5,
          errorMessage: 'โปรดระบุเลขที่หนังสือเดินทางให้ถูกต้อง',
        },
      ]}
      onChange={(e, val) => {
        const textValue = val.value
        const isValidatePassPort = isPassport(textValue) && textValue.length > 4
        setIdNoInputText(textValue)
        setButtonDisable(!isValidatePassPort)
      }}
    />
  )
}

const RenderInputNumber = (props) => {
  // Citizen no Input
  const { idNoInputText, setIdNoInputText, setButtonDisable } = props

  return (
    <NumberInputSearch
      id="search.app.id"
      value={idNoInputText}
      disabled={false}
      maxLength="20"
      customInput={Input}
      allowNegative={false}
      type="tel"
      pattern="[0-9]*"
      format="# #### ##### ###"
      validatetion={[
        {
          isValid:
            (idNoInputText.length > 0 && idNoInputText.length < 13) ||
            (!isIDCardNo(idNoInputText) && idNoInputText.length === 13),
          errorMessage: 'โปรดระบุเลขที่บัตรประชาชนให้ถูกต้อง',
        },
      ]}
      onChange={(e, val) => {
        const textValue = val.value
        const textValueWithoutSpace = textValue.replace(/\s/g, '')
        const isValidateCitizenNo = isIDCardNo(textValueWithoutSpace) && textValueWithoutSpace.length === 13

        setIdNoInputText(textValue)
        setButtonDisable(!isValidateCitizenNo)
      }}
    />
  )
}
const CITIZEN_ID = 'CITIZEN_ID'
const PASSPORT = 'PASSPORT'
const radioOptions = [
  {
    value: CITIZEN_ID,
    text: 'เลขที่บัตรประชาชน',
  },
  {
    value: PASSPORT,
    text: 'เลขที่หนังสือเดินทาง',
  },
]

const DialogBody = (props) => {
  const { selectedOption, setOption, setIdNoInputText, setButtonDisable } = props

  return (
    <div className="form-group">
      <RadioButtonGroup
        options={radioOptions}
        value={selectedOption}
        onChange={(e) => {
          setOption(e)
          setIdNoInputText('')
          setButtonDisable(true)
        }}
      />
      {selectedOption === CITIZEN_ID ? <RenderInputNumber {...props} /> : <RenderInputText {...props} />}
      <div className="validation-message attached-icon info-message form-control-feedback">
        <p>ระบบจะแสดงกรมธรรม์ที่มีอยู่ทั้งหมดของผู้ใช้เลขที่บัตรประชาชน หรือ เลขที่หนังสือเดินทาง</p>
      </div>
    </div>
  )
}

const CustomModal = styled(Modal)`
  .modal-title {
    flex: 1 0 auto;
    font-weight: bold;
    font-size: 1rem;
    color: ${({ theme }) => theme.variables['$brand-primary']};
    max-width: 80%;
    padding-left: 0.5rem;
  }
  .modal-body {
    padding: 15px 25px;
  }
  .form-group {
    margin-bottom: 1.5rem;
    margin-top: 0.5rem;
    .btn {
      margin-bottom: 0.25rem;
    }
  }
  .form-control-feedback {
    margin-top: 0.5rem;
  }
`

const _SearchAppByIdNoPopupComponent = ({ notificationMessage, onExit, ...props }) => {
  return (
    <CustomModal
      isOpen={notificationMessage !== 'None'}
      backdrop={true}
      size="lg"
      modalClassName="default-bootstrap"
      toggle={() => {
        props.setProcessingSuccess()
        if (!props.callingApi) {
          onExit()
        }
      }}
    >
      <DialogModalHeader {...props} onExit={!props.callingApi ? () => onExit() : null} />
      <ModalBody>
        <DialogBody {...props} />
        <ConfirmButton {...props} onExit={() => onExit()} />
      </ModalBody>
    </CustomModal>
  )
}

const mapStateSearchAppByIdPopupProps = (state) => {
  return { callingApi: state.cloneAppSearchStatus === 'searching' }
}

const SearchAppByIdNoModalContainer = ({ notificationMessage, onExit }) => {
  const dispatch = useDispatch()
  const { callingApi } = useSelector(mapStateSearchAppByIdPopupProps)

  const [selectedOption, setOption] = useState(CITIZEN_ID)
  const [idNoInputText, setIdNoInputText] = useState('')
  const [isDisabledButton, setButtonDisable] = useState(true)
  const [isCallSearch, setIsCallSearch] = useState(false)

  const handleSetProcessingSuccess = useCallback(() => {
    dispatch(setProcessingSuccess())
  }, [dispatch])

  const handleSetNotificationMessage = useCallback(
    (message) => {
      dispatch(setNotificationMessage(message))
    },
    [dispatch]
  )

  const handleSearch = useCallback(
    (params) => {
      dispatch(searchAppListById(params))
    },
    [dispatch]
  )

  return (
    <_SearchAppByIdNoPopupComponent
      notificationMessage={notificationMessage}
      onExit={onExit}
      callingApi={callingApi}
      selectedOption={selectedOption}
      setOption={setOption}
      idNoInputText={idNoInputText}
      setIdNoInputText={setIdNoInputText}
      isDisabledButton={isDisabledButton}
      setButtonDisable={setButtonDisable}
      isCallSearch={isCallSearch}
      setIsCallSearch={setIsCallSearch}
      setProcessingSuccess={handleSetProcessingSuccess}
      setNotificationMessage={handleSetNotificationMessage}
      onSearch={handleSearch}
    />
  )
}

export default SearchAppByIdNoModalContainer
